import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { getLenseConfigParser } from 'parser/pricingSimulator/lenseConfigParser';
import { LenseConfig, Scenario } from 'types/pricingSimulator';
import _ from 'lodash';
import useUpdateLastViewedLense from './lens/useUpdateLastViewedLense';

interface FetchLenseConfigProp extends HookInputProps {
    updateScenarioProperty: <K extends keyof Scenario>(key: K, value: Scenario[K]) => void;
    selectedScenarioInfo: Scenario | null;
    loggedInUser: string;
}

function useFetchLenseConfig({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    updateScenarioProperty,
    selectedScenarioInfo,
    loggedInUser,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);

    const { mutate: triggerLastViewed } = useUpdateLastViewedLense();

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData = getLenseConfigParser(payload);
            updateScenarioProperty('lenseConfig', parsedData);
            updateScenarioProperty('lenseConfigBackup', _.cloneDeep(parsedData));
            // find all the ticked lens selections
            const tickedLenses = parsedData?.filter((len: LenseConfig) => len.isSelected);
            let activeLenseItem = tickedLenses?.find((item) => item.lenseId === payload?.active_lens?.lens_id);
            // if active lens either null or not in the ticked list then take first one from ticked list as default
            if (!activeLenseItem && tickedLenses?.length) {
                activeLenseItem = tickedLenses[0];
                //  trigger api to update last viewed lens
                triggerLastViewed({
                    scenarioId: selectedScenarioInfo?.scenarioId,
                    userId: loggedInUser,
                    lensId: activeLenseItem?.lenseId,
                });
            }

            updateScenarioProperty('activeLense', {
                activeLenseId: activeLenseItem?.lenseId,
                activeLenseData: activeLenseItem,
            });

            updateScenarioProperty('offset', 0);

            //handle this in usefetchscenariodetails with saved information
            // updateScenarioProperty('sortObject', {
            //     sortBy: selectedScenarioInfo?.dimAttributes?.[0]?.value || '',
            //     sortingOrder: 'asc',
            //     sortType: 'actual',
            // });
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchLenseConfig };
